import Link from 'next/link'
import { FC, useState } from 'react'
import CartItem from '@components/cart/CartItem'
import { Button, Text } from '@components/ui'
import { useUI } from '@components/ui/context'
import SidebarLayout from '@components/common/SidebarLayout'
import useCart from '@framework/cart/use-cart'
import usePrice from '@framework/product/use-price'
import useCheckout from '@framework/checkout/use-checkout'
import ShippingWidget from '../ShippingWidget'
import PaymentWidget from '../PaymentWidget'
import s from './CheckoutSidebarView.module.scss'
import { useCheckoutContext } from '../context'
import { trackInitiateCheckout } from '@utils/facebook-pixel'
import { trackInitiateCheckout as trackOpenAIInitiateCheckout } from '@utils/openai-tracking'

const CheckoutSidebarView: FC = () => {
  const [loadingSubmit, setLoadingSubmit] = useState(false)
  const { setSidebarView, closeSidebar } = useUI()
  const { data: cartData, mutate: refreshCart } = useCart()
  const { data: checkoutData, submit: onCheckout } = useCheckout()
  const { clearCheckoutFields } = useCheckoutContext()

  async function handleSubmit(event: React.ChangeEvent<HTMLFormElement>) {
    try {
      setLoadingSubmit(true)
      event.preventDefault()

      // Track Facebook Pixel InitiateCheckout event
      if (cartData) {
        trackInitiateCheckout({
          totalPrice: cartData.totalPrice,
          currency: cartData.currency,
          lineItems: cartData.lineItems.map(item => ({
            productId: item.productId,
            name: item.name,
            quantity: item.quantity,
            variant: { title: item.variant?.name }
          }))
        })

        // Track OpenAI Pixel checkout_started event
        trackOpenAIInitiateCheckout({
          totalPrice: cartData.totalPrice,
          currency: cartData.currency,
          lineItems: cartData.lineItems.map(item => ({
            productId: item.productId,
            name: item.name,
            quantity: item.quantity,
            variant: { title: item.variant?.name }
          }))
        })
      }

      await onCheckout()
      clearCheckoutFields()
      setLoadingSubmit(false)
      refreshCart()
      closeSidebar()
    } catch {
      // TODO - handle error UI here.
      setLoadingSubmit(false)
    }
  }

  const { price: subTotal } = usePrice(
    cartData && {
      amount: Number(cartData.subtotalPrice),
      currencyCode: cartData.currency.code,
    }
  )
  const { price: total } = usePrice(
    cartData && {
      amount: Number(cartData.totalPrice),
      currencyCode: cartData.currency.code,
    }
  )

  return (
    <SidebarLayout
      className={s.root}
      handleBack={() => setSidebarView('CART_VIEW')}
    >
      <div className="">
        <Link href="/cart">
          <a>
            <Text variant="sectionHeading">Checkout</Text>
          </a>
        </Link>

        <PaymentWidget
          isValid={checkoutData?.hasPayment}
          onClick={() => setSidebarView('PAYMENT_VIEW')}
        />
        <ShippingWidget
          isValid={checkoutData?.hasShipping}
          onClick={() => setSidebarView('SHIPPING_VIEW')}
        />

        <ul className={s.lineItemsList}>
          {cartData!.lineItems.map((item: any) => (
            <CartItem
              key={item.id}
              item={item}
              currencyCode={cartData!.currency.code}
              variant="display"
            />
          ))}
        </ul>
      </div>

      <form
        onSubmit={handleSubmit}
        className={s.form}
      >
        <ul className="">
          <li className={s.totalItem}>
            <span>Subtotal</span>
            <span>{subTotal}</span>
          </li>
          <li className={s.totalItem}>
            <span>Taxes</span>
            <span>Calculated at checkout</span>
          </li>
          <li className={s.totalItem}>
            <span>Shipping</span>
            <span className="f">FREE</span>
          </li>
        </ul>
        <div className={s.totalContainer}>
          <span>Total</span>
          <span>{total}</span>
        </div>
        <div>
          {/* Once data is correctly filled */}
          <Button
            type="submit"
            width="100%"
            disabled={!checkoutData?.hasPayment || !checkoutData?.hasShipping}
            loading={loadingSubmit}
          >
            Confirm Purchase
          </Button>
        </div>
      </form>
    </SidebarLayout>
  )
}

export default CheckoutSidebarView
